import { deviceAdminApi } from '@/api';
import {
	ButtonDropdown,
	EditableCell,
	HelpFactoryComponent,
	ModalConfirmation,
	ModalConfirmationList,
	NotificationStatus,
	SearchColumn,
} from '@/components/common';
import {
	buildActionColumn,
	buildColumn,
	ColumnsProps,
	getDefaultTablePaginationConfig,
	getDefaultTableSelectionConfigPagination,
	getSelectedRowKeysPagination,
	getUniqueValuesArray,
	handleResponse,
	invertSelectedRowKeys,
	lockedValidation,
	ScrollType,
	useHandleOnChangeTableLogic,
} from '@/Helper';
import { getPermissionErrorMessage, SecuredComponents, SubPermissions, User } from '@/model/AccountModel';
import {
	BaseColumns,
	DefaultColumns,
	PaginationSetting,
	ResponseObject,
	ResponseObjectEntity,
	ResponseStatusCode,
	SelectedDeviceKeys,
	SelectOptions,
	SorterConfigEx,
} from '@/model/CommonModel';
import {
	ButtonBuilder,
	CurrentDeviceControlObj,
	DeviceObjectType,
	FunctionGroupCredential,
	OptionsButtonBuilder,
	SelectedRowKeyPagination,
	VelocityConfigurationModel,
} from '@/model/DeviceAdminModel';
import { FunctionGroup } from '@/model/EnrollmentModel';
import { HelpFeature } from '@/model/HelpModel';
import { useStoreDispatch, useStoreSelector } from '@/store';
import { setCurrentDevice } from '@/store/common/actions';
import { selectCurrentDevice } from '@/store/common/selectors';
import {
	clearVelocityConfigurationSelectionsAction,
	setFunctionGroupsBy,
	setFunctionGroupsByData,
	setVelocityConfigurationEditMode,
	setVelocityConfigurationFilterMode,
	setVelocitySelectedRowKeysPaginationAction,
} from '@/store/deviceControl/actions';
import { selectTablePaginationSetting, selectVelocityConfiguration } from '@/store/deviceControl/selectors';
import { CaretDownOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Form, notification, Space, Table } from 'antd';
import { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { SorterResult, TableRowSelection } from 'antd/es/table/interface';
import React, { useEffect, useRef, useState } from 'react';
import { batch } from 'react-redux';
import styles from './functionGroup.module.scss';
import { FunctionGroupDeleteWarning } from './FunctionGroupModal/FunctionGroupDeleteWarning/FunctionGroupDeleteWarning';
import { FunctionGroupModal } from './FunctionGroupModal/FunctionGroupModal';

type Props = {};

type FunctionGroupColumn = {} & BaseColumns & DefaultColumns;

//Avoid creating object style inline, since increases reconciliations
const user: User = getUser();
const componentPermission = User.getComponentPermission(user, SecuredComponents.Function_Group);
const componentSubPermission = User.getSubComponentPermission(user, SubPermissions.FunctionGroup_ViewFunctionGroupGeneralTab).allowed;
const scroll: ScrollType = { x: 379, y: 312 };
const maxLength: number = 32;
const deviceObjectType: DeviceObjectType = DeviceObjectType.FunctionGroup;
const topicId: string = 'FunctionGroups';

const createButtonOptions = (isItemOnSamePage: boolean, isMoreThanOneSelected: boolean): OptionsButtonBuilder<string> => {
	return {
		labelOrIcon: '...',
		options: [
			{
				//This only uses can view permissions since we disabled the Save button on the component itself, if it doesn't have permissions
				id: 'functionGroupsTableActionDropdownEditButton',
				label: _('Edit'),
				disabled: (!componentPermission.canView && !componentSubPermission) || isMoreThanOneSelected,
				value: 'edit',
				title: getPermissionErrorMessage(componentPermission.canView, false, isMoreThanOneSelected),
			},
			{
				id: 'functionGroupsTableActionDropdownRenameButton',
				label: _('Rename'),
				disabled: !componentPermission.canUpdate || !isItemOnSamePage || isMoreThanOneSelected,
				value: 'rename',
				title: getPermissionErrorMessage(componentPermission.canUpdate, false, isMoreThanOneSelected),
			},
			{
				id: 'functionGroupsTableActionDropdownDeleteButton',
				label: _('Delete'),
				disabled: !componentPermission.canDelete,
				value: 'delete',
				title: getPermissionErrorMessage(componentPermission.canDelete),
			},
		],
	};
};

const FunctionGroup: React.FC<Props> = () => {
	//#region State Management Variables
	const dispatch = useStoreDispatch();

	const [isItemOnSamePage, setIsItemOnSamePage] = useState<boolean>(false);
	const [functionGroupId, setFunctionGroupId] = useState<number>(undefined);
	const [functionGroupName, setFunctionGroupName] = useState<string>('');
	const [isFunctionGroupModalOpen, setIsFunctionGroupModalOpen] = useState<boolean>(false);
	const [form] = Form.useForm();
	const [editingKey, setEditingKey] = useState('');
	const [shouldResetSearchColumn, setShouldResetSearchColumn] = useState<boolean>(false);
	const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
	const [isTableLoading, setIsTableLoading] = useState<boolean>(false);
	const [tableData, setTableData] = useState<FunctionGroupColumn[]>([]);
	const [sorterConfigEx, setSorterConfigEx] = useState<SorterConfigEx>({ columnKey: undefined, order: undefined });

	const isSearchPerformed = useRef<boolean>(false);
	const redirectPage = useRef<boolean>(false);

	const handleOnChangeTableLogic = useHandleOnChangeTableLogic();

	const { isEditMode, functionGroups, isFilterMode, selectedRowKeysPagination }: VelocityConfigurationModel = useStoreSelector(selectVelocityConfiguration);
	const tablePaginationSetting: PaginationSetting = useStoreSelector<PaginationSetting>(selectTablePaginationSetting);
	const currentDevice: CurrentDeviceControlObj = useStoreSelector<CurrentDeviceControlObj>(selectCurrentDevice);
	//#endregion

	useEffect(() => {
		return () => {
			clearSelection();
		};
	}, []);

	useEffect(() => {
		const mapRes: FunctionGroupColumn[] = createData();
		setTableData(mapRes);
	}, [JSON.stringify(functionGroups)]);

	// * This hook ensure that always return a valid page number when no values has returned
	useEffect(() => {
		if (tablePaginationSetting.TotalItems === 0 && tablePaginationSetting.PageNumber > 1) {
			const { PageNumber: currentPage } = tablePaginationSetting;
			dispatch(
				setFunctionGroupsBy({
					...tablePaginationSetting,
					PageNumber: currentPage - 1,
				})
			);
		}
	}, [tablePaginationSetting]);

	const clearSelection = () => {
		dispatch(clearVelocityConfigurationSelectionsAction({ objectType: deviceObjectType }));
		setSelectedRowKeys([]);
	};

	const handleChangePagination = (page: number, pageSize: number) => {
		const isSamePage: boolean = selectedRowKeysPagination[0]?.PaginationPage === page;
		setIsItemOnSamePage(isSamePage);
		if (pageSize !== tablePaginationSetting.PageSize) {
			clearSelection();
		}
	};

	const pagination: TablePaginationConfig = getDefaultTablePaginationConfig(
		isEditMode,
		handleChangePagination,
		tablePaginationSetting.PageNumber,
		tablePaginationSetting.PageSize,
		tablePaginationSetting.TotalItems,
		undefined,
		selectedRowKeys
	);

	const onRemoveFunctionGroupLocks = async (functionGroup: SelectedDeviceKeys): Promise<void> => {
		await deviceAdminApi.unlockComponentDeviceAdmin(functionGroup.Id, SecuredComponents.Function_Group);
	};

	const onDeleteRequest = async (functionGroup: SelectedDeviceKeys): Promise<boolean> => {
		return deviceAdminApi
			.deleteFunctionGroups(functionGroup)
			.then(response => {
				if (!handleResponse(response)) {
					return true;
				}

				return false;
			})
			.finally(() => {
				return onRemoveFunctionGroupLocks(functionGroup);
			});
	};

	const onTryDeleteFunctionGroup = async (functionGroupsToDelete: SelectedDeviceKeys[]) => {
		let wereAnyDeleted: boolean = false;
		for (const fg of functionGroupsToDelete) {
			const response: ResponseObjectEntity<FunctionGroupCredential[]> = await deviceAdminApi.canDeleteFunctionGroup(fg);
			await new Promise<void>(resolve => {
				NotificationStatus({
					responseData: response,
					onSuccessCallback: async () => {
						wereAnyDeleted = (await onDeleteRequest(fg)) || wereAnyDeleted;
						resolve();
					},
					onAdditionalInfoRequiredCallback: async () => {
						const { Entity: data } = response;
						const content = <FunctionGroupDeleteWarning data={data} />;
						ModalConfirmation({
							title: (
								<label
									style={{ fontWeight: 'bold' }}
									dangerouslySetInnerHTML={{ __html: _('AreYouSureYouWantToDeleteFunctionGroup').replace('%1', `"${fg.Name}"`) }}
								/>
							),
							content,
							width: '644px',
							onConfirm: async () => {
								wereAnyDeleted = (await onDeleteRequest(fg)) || wereAnyDeleted;
								resolve();
							},
							onCancel: async () => {
								await onRemoveFunctionGroupLocks(fg);
								resolve();
							},
						});
					},
					notUseDefaultNotification: false,
					onComponentLockedCallback: () => resolve(),
					onPermissionErrorCallback: () => resolve(),
					onSystemErrorCallback: () => {
						notification['error']({
							message: response.ErrorMessage,
						});
						resolve();
					},
				});
			});
		}

		if (wereAnyDeleted) {
			clearSelection();
			dispatch(setFunctionGroupsBy(tablePaginationSetting));
		}
	};

	const setRedirectPage = (value: boolean) => {
		redirectPage.current = value;
	};

	const setSearchPerformed = (value: boolean) => {
		isSearchPerformed.current = value;
	};

	const handleVisibleFunctionGroupModal = (visible: boolean, refreshTable: boolean = false) => {
		batch(() => {
			setIsFunctionGroupModalOpen(visible);
			if (refreshTable) {
				dispatch(setFunctionGroupsBy(tablePaginationSetting));
			}
		});
	};

	const disabled: boolean = editingKey !== '';
	const isMoreThanOneSelected: boolean = selectedRowKeys.length > 1;
	const items = createButtonOptions(isItemOnSamePage, isMoreThanOneSelected);

	const handleOnSearchResults = (searchedValue: string) => {
		dispatch(setFunctionGroupsBy({ ...tablePaginationSetting, TotalItems: 0, PageNumber: 1, SearchedValue: searchedValue }));
	};

	const columns: ColumnsProps<FunctionGroupColumn>[] = [
		{
			...buildColumn(_('FunctionGroupName'), 'Name', 'auto', 'start'),
			key: 'Name',
			editable: true,
			sorter: !isEditMode,
			sortOrder: sorterConfigEx.columnKey === 'Name' && sorterConfigEx.order,
			...SearchColumn({
				maxLength,
				dataIndex: 'Name',
				reset: undefined,
				label: undefined,
				notVisible: isEditMode,
				resetSearch: shouldResetSearchColumn,
				setIsFilterMode: setVelocityConfigurationFilterMode,
				clearSelection,
				handleResetSearch: () => setShouldResetSearchColumn(false),
				setSearchResults: (searchedValue: string) => handleOnSearchResults(searchedValue),
				setSearchedValue: undefined,
				searchColumnId: undefined,
				setSearchPerformed: (value: boolean) => setSearchPerformed(value),
			}),
		},
	];

	if (isEditMode) {
		columns.unshift(buildActionColumn(disabled));
	}

	const onChangeSelection = (key: BaseColumns) => {
		const id = key.key.toString();
		if (selectedRowKeys.findIndex(key => key === id) === -1) {
			const cloneState: FunctionGroup[] = functionGroups.map(x =>
				x.FunctionGroupId.toString() === id ? { ...x, checked: true } : { ...x, checked: false }
			);
			batch(() => {
				setIsItemOnSamePage(true);
				dispatch(setFunctionGroupsByData(cloneState));
				setSelectedRowKeys([id]);
			});
		}
	};

	const contextMenuOptions: SelectOptions<string>[] = [...items?.options];
	const isEditing = (record: FunctionGroupColumn) => record.key.toString() === editingKey;

	const mergedColumns = columns.map(col =>
		!col.editable
			? {
					...col,
					onCell: (record: FunctionGroupColumn) => ({
						record,
						dataIndex: col.dataIndex,
						title: col.title,
						maxLength,
						options: contextMenuOptions,
						isTableInEditMode: isEditMode,
						onChangeSelection,
						onClickOption: handleActionScope,
					}),
			  }
			: {
					...col,
					onCell: (record: FunctionGroupColumn) => ({
						record,
						dataIndex: col.dataIndex,
						title: col.title,
						maxLength,
						options: contextMenuOptions,
						isTableInEditMode: isEditMode,
						onChangeSelection,
						onClickOption: handleActionScope,
						inputType: 'text',
						editing: isEditing(record),
					}),
			  }
	);

	const getSelectedFunctionGroups = async (): Promise<SelectedDeviceKeys[]> => {
		const response = await deviceAdminApi.getVelocityDevicesBySelectedKeys(deviceObjectType, selectedRowKeys as string[]);
		if (handleResponse(response)) {
			return [];
		}

		return response.Entity;
	};

	const handleActionScope = async (key: string) => {
		const selectedFunctionGroupsKeys = await getSelectedFunctionGroups();
		const selectedFunctionGroups = selectedFunctionGroupsKeys.map(x => ({ FunctionGroupId: x.Id, Name: x.Name } as Partial<FunctionGroup>));
		switch (key) {
			case 'delete': {
				const mappedDataToDelete = selectedFunctionGroupsKeys.filter(fGroup => selectedRowKeys.some(item => parseInt(item.toString()) === fGroup.Id));
				ModalConfirmationList({
					type: _('FunctionGroup'),
					data: mappedDataToDelete.map(item => ({ Name: item.Name })),
					onConfirm: () => onTryDeleteFunctionGroup(mappedDataToDelete),
				});
				break;
			}
			case 'rename': {
				const { FunctionGroupId } = selectedFunctionGroups[0];
				dispatch(setVelocityConfigurationEditMode(true));
				const findKey: FunctionGroupColumn = tableData.find(row => row.key.toString() === FunctionGroupId.toString());
				edit(findKey);
				const cloneState: FunctionGroup[] = changeStateProps(FunctionGroupId, { editable: true });
				dispatch(setFunctionGroupsByData(cloneState));
				break;
			}
			case 'edit': {
				const { FunctionGroupId, Name } = selectedFunctionGroups[0];
				const isLocked: boolean = await lockedValidation(deviceObjectType, FunctionGroupId, false);
				if (isLocked) {
					return;
				}
				setFunctionGroupId(FunctionGroupId);
				setFunctionGroupName(Name);
				dispatch(
					setCurrentDevice({
						...currentDevice,
						Id: FunctionGroupId,
					})
				);
				handleVisibleFunctionGroupModal(true);
				break;
			}
		}
	};

	const changeStateProps = (id: number, functionGroup: Partial<FunctionGroup>): FunctionGroup[] => {
		const cloneState: FunctionGroup[] = [...functionGroups];
		const index = cloneState.findIndex(x => x.FunctionGroupId === id);
		if (~index) {
			const item = cloneState[index];
			cloneState.splice(index, 1, { ...item, ...functionGroup });
		}

		return cloneState;
	};

	const edit = (record: FunctionGroupColumn) => {
		form.setFieldsValue({
			Name: '',
			...record,
		});
		setEditingKey(record.key.toString());
	};

	const resetSearchColumn = () => {
		if (isFilterMode) {
			setShouldResetSearchColumn(true);
		}
	};

	const getSelectedRowKeysForSelectAll = async (newSelectedRowKeys: React.Key[]): Promise<React.Key[]> => {
		const response = await deviceAdminApi.getFunctionGroupIds();
		if (!handleResponse(response)) {
			const functionGroupsIds = response.Entity?.map<string>(x => x.toString());
			return getUniqueValuesArray(newSelectedRowKeys, functionGroupsIds);
		}
		return [];
	};

	const handleChange = (selectedRowKeys: React.Key[]) => {
		const cloneState: FunctionGroup[] = functionGroups.map(x => ({ ...x, checked: selectedRowKeys.findIndex(r => r === x.FunctionGroupId) !== -1 }));
		const selectedKeysPagination: SelectedRowKeyPagination[] = getSelectedRowKeysPagination(
			selectedRowKeys,
			selectedRowKeysPagination,
			tablePaginationSetting.PageNumber,
			setIsItemOnSamePage
		);
		dispatch(setVelocitySelectedRowKeysPaginationAction(selectedKeysPagination));
		dispatch(setFunctionGroupsByData(cloneState));
		setSelectedRowKeys(selectedRowKeys);
	};

	const handleSelectAll = async () => {
		setIsTableLoading(true);
		const newSelectedRowKeys = await getSelectedRowKeysForSelectAll(selectedRowKeys);
		const cloneState: FunctionGroup[] = functionGroups.map(x => ({
			...x,
			checked: newSelectedRowKeys.findIndex(r => r === x.FunctionGroupId?.toString()) !== -1,
		}));
		dispatch(setFunctionGroupsByData(cloneState));
		setSelectedRowKeys(newSelectedRowKeys);
		setIsTableLoading(false);
	};

	const handleSelectInvert = () => {
		const dataKeys = functionGroups.map<React.Key>(x => x.FunctionGroupId?.toString());
		const newSelectedRowKeys = invertSelectedRowKeys(dataKeys, selectedRowKeys);
		const cloneState: FunctionGroup[] = functionGroups.map(x => ({
			...x,
			checked: newSelectedRowKeys.findIndex(key => key === x.FunctionGroupId?.toString()) !== -1,
		}));
		dispatch(setFunctionGroupsByData(cloneState));
		setSelectedRowKeys(newSelectedRowKeys);
	};

	const handleOnSaveEditRow = async (id: number) => {
		const isLocked: boolean = await lockedValidation(deviceObjectType, id, false);
		if (isLocked) {
			setEditingKey('');
			const cloneState: FunctionGroup[] = changeStateProps(id, { editable: false });
			dispatch(setFunctionGroupsByData(cloneState));
			dispatch(setVelocityConfigurationEditMode(false));
			return;
		}

		const name: string = form.getFieldValue('Name');
		const nameError: ResponseObject = await deviceAdminApi.renameDevice(deviceObjectType, name.trim(), id);
		if (nameError.ResponseStatusCode !== ResponseStatusCode.Success && nameError.ErrorMessage) {
			form.setFields([
				{
					name: 'Name',
					errors: [nameError.ErrorMessage],
				},
			]);
			return;
		} else if (nameError.ResponseStatusCode === ResponseStatusCode.PermissionError) {
			notification['error']({
				message: nameError.ResponseErrorDescription,
			});
			setEditingKey('');
			dispatch(setVelocityConfigurationEditMode(false));
			dispatch(setFunctionGroupsBy(tablePaginationSetting));
			return;
		}
		setEditingKey('');
		dispatch(setVelocityConfigurationEditMode(false));
		resetSearchColumn();
		setRedirectPage(true);
		dispatch(setFunctionGroupsBy(tablePaginationSetting));
	};

	const handleOnCancelEditRow = (id: number) => {
		dispatch(setVelocityConfigurationEditMode(false));
		const cloneState: FunctionGroup[] = changeStateProps(id, { editable: false });
		dispatch(setFunctionGroupsByData(cloneState));
		setEditingKey('');
	};

	const createActionElement = (functionGroup: FunctionGroup): React.ReactNode => {
		let content: React.ReactNode = undefined;
		if (functionGroup.editable) {
			const id = functionGroup.FunctionGroupId;
			content = (
				<>
					<Button id='renameSaveButton' key='save' type='primary' onClick={() => handleOnSaveEditRow(id)}>
						{_('Save')}
					</Button>
					<Button id='renameCancelButton' key='cancel' onClick={() => handleOnCancelEditRow(id)}>
						{_('Cancel')}
					</Button>
				</>
			);
		}
		return <div className={styles.actions}>{content}</div>;
	};

	const createData = (): FunctionGroupColumn[] => {
		if (functionGroups.length > 0) {
			return functionGroups.map<FunctionGroupColumn>((data: FunctionGroup) => ({
				key: data.FunctionGroupId.toString(),
				Action: createActionElement(data),
				Name: data.Name,
			}));
		}

		return [];
	};

	const buttonsBuilder = (button: ButtonBuilder[]): React.ReactNode =>
		button.map(x => (
			<Button id={x.id} key={x.label} onClick={x.onClick} disabled={x.disabled} title={x.title} type='primary'>
				{x.icon} {x.label}
			</Button>
		));

	const buttons: ButtonBuilder[] = [
		{
			label: _('AddFunctionGroup'),
			icon: <PlusOutlined />,
			disabled: !componentPermission.canAdd || isEditMode,
			onClick: () => {
				batch(() => {
					setIsFunctionGroupModalOpen(true);
					setFunctionGroupId(undefined);
					setFunctionGroupName('');
				});
			},
			id: 'functionGroupAddButton',
			title: getPermissionErrorMessage(componentPermission.canAdd),
		},
	];

	const rowSelection: TableRowSelection<FunctionGroupColumn> = {
		preserveSelectedRowKeys: true,
		type: 'checkbox',
		selections: getDefaultTableSelectionConfigPagination(disabled, handleSelectAll, handleSelectInvert),
		getCheckboxProps: record => ({
			id: `functionGroupTableCheckbox-${record.key}`,
			disabled,
		}),
		selectedRowKeys,
		onChange: handleChange,
	};

	const handleOnChangeTable = (pagination: TablePaginationConfig, filters: { Name?: string[] }, sorter: SorterResult<FunctionGroupColumn>) => {
		const { current, pageSize, shouldUpdateSearchResults, shouldUpdateTableResults, sortField, sortOrder } = handleOnChangeTableLogic({
			clearSelection,
			filters,
			handleChangePagination,
			isSearchPerformed,
			pagination,
			shouldResetSearchColumn,
			sorter,
			tablePaginationSetting,
		});
		setSorterConfigEx({ columnKey: sorter.columnKey?.toString(), order: sorter.order });

		if (shouldUpdateTableResults || shouldUpdateSearchResults) {
			dispatch(
				setFunctionGroupsBy({
					...tablePaginationSetting,
					PageNumber: current,
					PageSize: pageSize,
					SortDirection: sortOrder,
					SortField: sortField.toString(),
				})
			);
		}
	};

	const disabledActionButtons = disabled || selectedRowKeys.length === 0;

	return (
		<div className={styles.container}>
			<div className={styles.buttonContainer}>
				<Space>
					<ButtonDropdown
						id={'functionGroupsTableActionDropdown'}
						disabled={disabledActionButtons}
						menuOptions={items.options}
						icon={<CaretDownOutlined />}
						labelIcon={items.labelOrIcon}
						onClickOption={handleActionScope}
					/>
					{buttonsBuilder(buttons)}
				</Space>
				<HelpFactoryComponent helpFeature={HelpFeature.FunctionGroups} topicId={topicId} size='medium' />
			</div>
			<Form form={form}>
				<Table
					id='functionGroupsTable'
					columns={mergedColumns as ColumnsType<FunctionGroupColumn>}
					components={{
						body: {
							cell: EditableCell,
						},
					}}
					dataSource={tableData}
					pagination={pagination}
					scroll={scroll}
					size='small'
					className={styles.statusWidgetTable}
					rowSelection={rowSelection}
					onChange={handleOnChangeTable}
					loading={isTableLoading}
				/>
			</Form>
			{isFunctionGroupModalOpen && (
				<FunctionGroupModal onSetVisible={handleVisibleFunctionGroupModal} functionGroupId={functionGroupId} functionGroupName={functionGroupName} />
			)}
		</div>
	);
};

export { FunctionGroup };
